import { useRouter } from 'next/router';
import { memo, type ReactNode } from 'react';

import { NotFound } from '~/components/NotFound/NotFound';
import { PrivateWorkspaceAgentAssetListContent } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListContent';
import { PrivateWorkspaceAgentAssetListHeader } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListHeader';
import { PrivateWorkspaceAgentAssetListProvider } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListProvider';
import { PrivateWorkspaceAgentAssetListSeoTitle } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListSeoTitle';
import { PrivateWorkspaceAgentAssetListTabBar } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListTabBar';
import { QueryParamNames } from '~/constants/search';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type PrivateWorkspaceAgentAssetListPageProps = {
  artifactId?: string;
  children?: ReactNode;
  sessionId?: string;
};

const readQueryId = (value: string | string[] | undefined) => (typeof value === 'string' ? value : undefined);

/** Route or inline-modal body for one agent asset list artifact. */
export const PrivateWorkspaceAgentAssetListPage = memo(
  ({ artifactId: artifactIdProp, children, sessionId: sessionIdProp }: PrivateWorkspaceAgentAssetListPageProps) => {
    const router = useRouter();
    const sessionId =
      sessionIdProp ?? readQueryId(router.query.sessionId) ?? readQueryId(router.query[QueryParamNames.chatId]);
    const artifactId = artifactIdProp ?? readQueryId(router.query.artifactId);
    const { currentWorkspace } = useCurrentWorkspace();

    if (router.isReady && (!sessionId || !artifactId)) {
      return <NotFound />;
    }

    return (
      <PrivateWorkspaceAgentAssetListProvider
        artifactId={artifactId}
        sessionId={sessionId}
        workspaceId={currentWorkspace?.id}
      >
        <PrivateWorkspaceAgentAssetListSeoTitle />
        <PrivateWorkspaceAgentAssetListTabBar />
        <PrivateWorkspaceAgentAssetListHeader />
        <PrivateWorkspaceAgentAssetListContent />
        {children}
      </PrivateWorkspaceAgentAssetListProvider>
    );
  },
);

PrivateWorkspaceAgentAssetListPage.displayName = 'PrivateWorkspaceAgentAssetListPage';
